import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import * as dates from '@simplybenefits/sb-dates';
import { Text, TextInput, PhoneInput, NumberInput, Button, BubbleSelectBlock, ContentBlock, ContainerControlSite, Content, Loader, Modal, Icons, Theme } from '@simplybenefits/sb-ui-library';
import Header from '../../../components/Header.js';
import AccountInformation from '../components/AccountInformation.js';
import * as notifier from '../../../actions/notifier.js';
import validateForm from './validateForm.js';
import RenewalBenefitAccordion from './RenewalBenefitAccordion.js';
import RenewalHistoricalAccordion from './RenewalHistoricalAccordion.js';
import Icon from '@mui/material/Icon';
import { getRenewalFormData, getPreviousRenewalFormData } from '../../../actions/accounts.js';

/**
 * 
 */
const RenewalForm = ({ renewal, accountMeta, classes, close, onSubmit, dispatch, loader }) => {
    const [ isSameStopLossChargeAllClasses, setIsSameStopLossChargeAllClasses ] = useState(false);
    const [ sameStopLossCharge, setSameStopLossCharge ] = useState(0);
    const [ showGeneratingModal, setShowGeneratingModal ] = useState(false);
    const [ errors, setErrors ] = useState(null);
    const [ formState, setFormState ] = useState(null);
    const [ isPreviousReportDataSet, setIsPreviousReportDataSet ] = useState(false);

    const periodEndYear = new Date(renewal.periodEnd * 1000).getFullYear();

    const styles = {
        section: {
            marginBottom: 30
        },
        labelGrouping: {
            display: 'flex',
            flexDirection: 'column'
        },
        row: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 30
        }
    };

    const benefitTypes = [
        "life",
        "add",
        "ci",
        "ciSpouse", 
        "ciChild",
        "depLife",
        "ltd",
        "std",
        "health",
        "outOfCountry",
        "dental", 
        "efap",
        "financialAwareness",
        "virtualHealthcare"
    ];

    /**
     * Initialize formData with default values and values from plans
     */
    const initializeForm = async () => {
        const initializedData = await dispatch(getRenewalFormData(accountMeta.accountID, renewal.renewalID));
        setFormState(initializedData);
    };

    useEffect(() => {
        initializeForm();
    }, []);


    /**
    * Builds an array of breadcrumb objects representing the navigation path for the current page.
    * @returns {Array<Object>} An array of breadcrumb objects, each containing a title and a link or an onClick handler.
    */
    const _buildBreadcrumbs = () => [
        {
            title: 'Accounts',
            link: `/accounts`
        },
        {
            title: accountMeta.company,
            link: `/accounts/${ accountMeta.accountID }`
        },
        {
            title: 'Renewals',
            link: `/accounts/${ accountMeta.accountID }/renewals`
        },
        {
            title: `Renewal - ${ dates.formatDateForDisplay(renewal.renewalDate, 'll') }`,
            onClick: close()
        }
    ];

    /**
     * Updates a nested state object at a specific path, ensuring immutability.
     * @param state The current state object to be updated. It can be of any structure.
     * @param {Array<string>} keys An array of strings representing the keys to traverse through the nested state. The last key represents the property to be updated.
     * @param value The value to set at the nested path specified by the keys.
     * @returns A new state object with the value set at the specified path
     */
    const _setNestedState = (state, keys, value) => {
        let currentState = { ...state }; // Shallow Copy
        let currentLevel = currentState;

        keys.forEach((key, index) => {
            if (index === keys.length - 1) {
                currentLevel[key] = value;
            } else {
                currentLevel[key] = { ...currentLevel[key] };
            }
        
            currentLevel = currentLevel[key];
        });

        return currentState;
    };

    /**
    * Handles changes to form inputs and updates the state accordingly.
    * @param {Array<string>} nestedKeys An array of strings representing the keys to update in the nested state.
    * @param {Event} event The event object triggered by the input change.
    */
    const _handleInputChange = useCallback((nestedKeys, event) => {
        const value = event.target.value;
        setFormState((prevState) => _setNestedState(prevState, nestedKeys, value));
    }, []);

    /**
     * Normalizes the submission data
     * @returns {Object} A new object containing the normalized form state, 
     */
    const _normalizeSubmissionData = () => {
        const normalizedPremiumIllustrations = { ...formState.premiumIllustration };

        // Do not require numberOfPaidClaims or totalAmountOfClaimsPaid for these benefits
        const benefits = [ 'health', 'dental', 'efap', 'financialAwareness', 'virtualHealthcare' ];

        for (const benefit of benefits) {
            const benefitData = normalizedPremiumIllustrations[benefit];

            if (benefitData) {
                for (const classData of Object.values(benefitData)) {
                    if (!classData.numberOfPaidClaims) {
                        classData.numberOfPaidClaims = null;
                    }

                    if (!classData.totalAmountOfClaimsPaid) {
                        classData.totalAmountOfClaimsPaid = null;
                    }
                }
            }
        }
    
        return {
            ...formState,
            financialInformation: {
                rateCap: formState.financialInformation.rateCap || null,
                rateCapConditions: formState.financialInformation.rateCapConditions || null
            },
            premiumIllustration: normalizedPremiumIllustrations
        };
    };

    /**
     * Submits the renewal form after validating the form state.
     * @returns {Promise<void>} 
     */
    const _submitRenewalForm = async () => {
        // ValidationErrors is an object of input errors
        const { hasErrors, validationErrors } = await validateForm(formState, setErrors);
        setErrors(validationErrors);

        if (!hasErrors) {
            const normalizedSubmissionData = _normalizeSubmissionData();
            onSubmit(normalizedSubmissionData);
            setShowGeneratingModal(true);
        } else {
            await dispatch(notifier.displayError({ verbiage: 'Please confirm that you have filled out all the details.' }));
        }
    };

    /**
     * Gets the data from the previously generated renewal report and sets it in state .
     * @returns {Promise<void>} 
     */
    const _togglePreviousRenewalData = async () => {
        if (isPreviousReportDataSet) {
            await initializeForm();
            setIsPreviousReportDataSet(false);
            return;
        }

        const initializedData = await dispatch(getPreviousRenewalFormData(accountMeta.accountID, renewal.renewalID));

        if (initializedData) {
            setFormState(initializedData);
            setIsPreviousReportDataSet(true);
        }
    };

    /**
     * Updates the stop loss charge percentage for all classes
     * @param {number} value
     */
    const _handleSameStopLossChargeChange = (value) => {
        setFormState((prevState) => {
            const updatedClasses = { ...prevState.experienceAnalysis.classes };
    
            for (const classID of Object.keys(updatedClasses)) {
                updatedClasses[classID].ehcStopLossChargePercentage = value;
            }
    
            return {
                ...prevState,
                experienceAnalysis: {
                    ...prevState.experienceAnalysis,
                    classes: updatedClasses
                }
            };
        });

        setSameStopLossCharge(value);
    };

    /**
     * Toggles sameStopLoss and updates the stop loss charge percentage for all classes.
     */
    const _toggleSameStopLossCharge = (isToggled) => {
        setIsSameStopLossChargeAllClasses(isToggled);

        if (isToggled) { // initial set of same stop loss
            _handleSameStopLossChargeChange(sameStopLossCharge); 

            // Remove any previous errors when toggling same stop loss charge for all
            if (!errors) {
                return;
            }

            setErrors((prevState) => {
                const updatedClasses = { ...prevState.experienceAnalysis.classes };
        
                for (const classID of Object.keys(updatedClasses)) {
                    updatedClasses[classID].ehcStopLossChargePercentage = '';
                }
        
                return {
                    ...prevState,
                    experienceAnalysis: {
                        ...prevState.experienceAnalysis,
                        classes: updatedClasses
                    }
                };
            });
        } 
    };

    if (loader.isLoading || !formState) {
        return (
            <Loader/>
        );
    }

    return (
        <ContainerControlSite>
            <Header
                subHeader={ 'Generate Renewal Report' }
                breadcrumbs={_buildBreadcrumbs()}
                selectedTitle="accounts"
            />
            <Content>
                <AccountInformation accountMeta={ accountMeta } />

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 30, marginBottom: 25, gap: 15 }}>
                    <Button
                        onClick={ close() }
                        transparent
                    >
                        Back to Renewal Overview
                    </Button>
                    { renewal.hasFormData
                        ? (
                            <Button
                                color={ 'white' }
                                onClick={ () => _togglePreviousRenewalData() }
                            >
                                { isPreviousReportDataSet ? 'Reset Form Data' : 'Load Previous Report Data' }
                            </Button>
                        )
                        : null
                    }
                    <Button
                        color={ 'primary' }
                        onClick={ () => _submitRenewalForm() }
                    >
                        Submit
                    </Button>
                </div>

                <ContentBlock>
                    <div style={ styles.section }>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <Text large bold>
                                Generate Renewal Report
                            </Text>
                            <Text bold mediumLarge color={ 'primary' }>
                                { renewal.status}
                            </Text>
                        </div>

                        <div style={{ marginBottom: 30 }}>
                            <Text small>
                                {'\u2022'} The provided details are solely for generating the report (PDF) and rate confirmation summary (CSV). They are not used for any other purpose and are not applied to the group in any way.
                            </Text>
                            <Text small>
                                {'\u2022'} The proposed rates calculated by the system in the report are not applied to the group. Rate changes are managed through a separate process, which is not outlined on this page.
                            </Text>
                            <Text small>
                                {'\u2022'} This process is designed specifically for Wawanesa renewals; calculations for other carriers may vary.
                            </Text>
                            <Text small>
                                {'\u2022'} Applicable only to standard, fully-insured groups. Not compatible with associated plans, ASO groups, or Modular Flex plans.
                            </Text>
                        </div>

                        <div style={{ ...styles.labelGrouping, marginBottom: 30 }}>
                            <Text bold medium>
                                Renewal ID
                            </Text>
                            <Text medium>
                                { renewal.uid }
                            </Text>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                            <div style={ styles.labelGrouping }>
                                <Text bold medium>
                                    Renewal Date
                                </Text>
                                <Text medium>
                                    { dates.formatDateForDisplay(renewal.renewalDate, 'll') } 
                                </Text>
                            </div>

                            <div style={ styles.labelGrouping }>
                                <Text bold medium>
                                    Experience Period Start
                                </Text>
                                <Text medium>
                                    { dates.formatDateForDisplay(renewal.periodStart, 'll') } 
                                </Text>
                            </div>

                            <div style={ styles.labelGrouping }>
                                <Text bold medium>
                                    Experience Period End
                                </Text>
                                <Text medium>
                                    { dates.formatDateForDisplay(renewal.periodEnd, 'll') } 
                                </Text>
                            </div>

                            <div style={ styles.labelGrouping }>
                                <Text bold medium>
                                    Renewal Pickup Date
                                </Text>
                                <Text medium>
                                    { dates.formatDateForDisplay(renewal.renewalPickupDate, 'll') } 
                                </Text>
                            </div>

                            <div style={ styles.labelGrouping }>
                                <Text bold medium>
                                    Date Created
                                </Text>
                                <Text medium>
                                    { dates.formatDateForDisplay(renewal.dateAdded, 'll') }  
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div style={ styles.section }>
                        <Text mediumLarge bold color={ 'primary' }>
                            General
                        </Text>

                        <div style={ styles.row }>
                            <TextInput
                                label={ 'Sales Representative Name' }
                                value={ formState.general.salesRepName }
                                onChange={ (event) => _handleInputChange([ 'general', 'salesRepName' ], event) }
                                error={ Boolean(errors?.general?.salesRepName) }
                                helperText={ errors?.general?.salesRepName }
                                fullWidth
                            />

                            <TextInput
                                label={ 'Sales Representative Email' }
                                value={ formState.general.salesRepEmail }
                                onChange={ (event) => _handleInputChange([ 'general', 'salesRepEmail' ], event) }
                                error={ Boolean(errors?.general?.salesRepEmail) }
                                helperText={ errors?.general?.salesRepEmail }
                                fullWidth
                            />

                            <PhoneInput
                                label={ 'Sales Representative Phone Number' }
                                value={ formState.general.salesRepPhone }
                                onChange={ (event) => _handleInputChange([ 'general', 'salesRepPhone' ], event) }
                                error={ Boolean(errors?.general?.salesRepPhone) }
                                helperText={ errors?.general?.salesRepPhone }
                                fullWidth
                            />
                        </div>
                    </div>
                
                    <div style={ styles.section }>
                        <Text mediumLarge bold color={ 'primary' } customStyle={{ marginBottom: 8 }}>
                            Premium Illustration and Claims Overview
                        </Text>

                        <Text small customStyle={{ marginBottom: 20 }}>
                            Carrier change percentage is applied to the net rate, and any applicable discount will be deducted from the carrier change percentage.
                        </Text>

                        {
                            benefitTypes.map((benefitType) => (
                                <RenewalBenefitAccordion
                                    key={ benefitType }
                                    benefitType={ benefitType }
                                    benefitClasses={ formState.premiumIllustration[benefitType] }
                                    classMeta={ classes }
                                    handleInputChange={ _handleInputChange }
                                    errors={ errors }
                                />                          
                            ))
                        }

                        <Text medium bold customStyle={{ marginBottom: 10, marginTop: 20 }}>
                            Rate Cap
                        </Text>
                        <NumberInput
                            label={ 'Rate Cap' }
                            value={ formState.financialInformation.rateCap }
                            onChange={ (event) => _handleInputChange([ 'financialInformation', 'rateCap' ], event) }
                            suffix="%"
                            customStyle={{ width: '33%' }}
                        />
                        <TextInput
                            label={ 'Rate Cap Conditions' }
                            value={ formState.financialInformation.rateCapConditions }
                            onChange={ (event) => _handleInputChange([ 'financialInformation', 'rateCapConditions' ], event) }
                            fullWidth
                        />
                        <Text small customStyle={{ marginTop: 12 }}>
                            Please note that the rate cap conditions are displayed in the final report.
                        </Text>
                    </div>

                    <div style={ styles.section }>
                        <Text mediumLarge bold color={ 'primary' } customStyle={{ marginBottom: 11 }}>
                            Experience Analysis
                        </Text>

                        <Text mediumLarge bold customStyle={{ marginBottom: 18 }}>
                            { periodEndYear }
                        </Text>

                        <Text medium color={ 'primary' } customStyle={{ marginBottom: 12 }}>
                            All Classes
                        </Text>

                        <div style={ styles.row }>
                            <NumberInput
                                label={ 'EHC Target Loss Ratio' }
                                value={ formState.experienceAnalysis.ehc.targetLossRatioPercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'ehc', 'targetLossRatioPercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.ehc?.targetLossRatioPercentage) }
                                helperText={ errors?.experienceAnalysis?.ehc?.targetLossRatioPercentage }
                                suffix="%"
                                fullWidth
                            />
                            <NumberInput
                                label={ 'Dental Target Loss Ratio' }
                                value={ formState.experienceAnalysis.dental.targetLossRatioPercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'dental', 'targetLossRatioPercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.dental?.targetLossRatioPercentage) }
                                helperText={ errors?.experienceAnalysis?.dental?.targetLossRatioPercentage }
                                suffix="%"
                                fullWidth
                            />
                            <NumberInput
                                label={ 'STD Target Loss Ratio' }
                                value={ formState.experienceAnalysis.std.targetLossRatioPercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'std', 'targetLossRatioPercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.std?.targetLossRatioPercentage) }
                                helperText={ errors?.experienceAnalysis?.std?.targetLossRatioPercentage }
                                suffix="%"
                                fullWidth
                            />
                        </div>

                        <div style={ styles.row }>
                            <NumberInput
                                label={ 'EHC IBNR Rate' }
                                value={ formState.experienceAnalysis.ehc.ibnrRatePercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'ehc', 'ibnrRatePercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.ehc?.ibnrRatePercentage) }
                                helperText={ errors?.experienceAnalysis?.ehc?.ibnrRatePercentage }
                                suffix="%"
                                allowNegative
                                fullWidth
                            />
                            <NumberInput
                                label={ 'Dental IBNR Rate' }
                                value={ formState.experienceAnalysis.dental.ibnrRatePercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'dental', 'ibnrRatePercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.dental?.ibnrRatePercentage) }
                                helperText={ errors?.experienceAnalysis?.dental?.ibnrRatePercentage }
                                suffix="%"
                                allowNegative
                                fullWidth
                            />
                            <NumberInput
                                label={ 'STD IBNR Rate' }
                                value={ formState.experienceAnalysis.std.ibnrRatePercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'std', 'ibnrRatePercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.std?.ibnrRatePercentage) }
                                helperText={ errors?.experienceAnalysis?.std?.ibnrRatePercentage }
                                suffix="%"
                                allowNegative
                                fullWidth
                            />
                        </div>

                        <div style={ styles.row }>
                            <NumberInput
                                label={ 'Previous Year EHC IBNR' }
                                value={ formState.experienceAnalysis.ehc.ibnrRatePreviousPercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'ehc', 'ibnrRatePreviousPercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.ehc?.ibnrRatePreviousPercentage) }
                                helperText={ errors?.experienceAnalysis?.ehc?.ibnrRatePreviousPercentage }
                                prefix="$"
                                allowNegative
                                fullWidth
                            />
                            <NumberInput
                                label={ 'Previous Year Dental IBNR' }
                                value={ formState.experienceAnalysis.dental.ibnrRatePreviousPercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'dental', 'ibnrRatePreviousPercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.dental?.ibnrRatePreviousPercentage) }
                                helperText={ errors?.experienceAnalysis?.dental?.ibnrRatePreviousPercentage }
                                prefix="$"
                                allowNegative
                                fullWidth
                            />
                            <NumberInput
                                label={ 'Previous Year STD IBNR' }
                                value={ formState.experienceAnalysis.std.ibnrRatePreviousPercentage }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'std', 'ibnrRatePreviousPercentage' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.std?.ibnrRatePreviousPercentage) }
                                helperText={ errors?.experienceAnalysis?.std?.ibnrRatePreviousPercentage }
                                prefix="$"
                                allowNegative
                                fullWidth
                            />
                        </div>

                        <div style={{ ...styles.row, width: '66%' }}>
                            <NumberInput
                                label={ 'EHC Trend' }
                                value={ formState.experienceAnalysis.ehc.trend }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'ehc', 'trend' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.ehc?.trend) }
                                helperText={ errors?.experienceAnalysis?.ehc?.trend }
                                suffix="%"
                                fullWidth
                            />
                            <NumberInput
                                label={ 'Dental Trend' }
                                value={ formState.experienceAnalysis.dental.trend }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'dental', 'trend' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.dental?.trend) }
                                helperText={ errors?.experienceAnalysis?.dental?.trend }
                                suffix="%"
                                fullWidth
                            />
                        </div>

                        <div style={ styles.row }>
                            <NumberInput
                                label={ 'EHC Credibility' }
                                value={ formState.experienceAnalysis.ehc.credibility }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'ehc', 'credibility' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.ehc?.credibility) }
                                helperText={ errors?.experienceAnalysis?.ehc?.credibility }
                                suffix="%"
                                fullWidth
                            />
                            <NumberInput
                                label={ 'Dental Credibility' }
                                value={ formState.experienceAnalysis.dental.credibility }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'dental', 'credibility' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.dental?.credibility) }
                                helperText={ errors?.experienceAnalysis?.dental?.credibility }
                                suffix="%"
                                fullWidth
                            />
                            <NumberInput
                                label={ 'STD Credibility' }
                                value={ formState.experienceAnalysis.std.credibility }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'std', 'credibility' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.std?.credibility) }
                                helperText={ errors?.experienceAnalysis?.std?.credibility }
                                suffix="%"
                                fullWidth
                            />
                        </div>

                        <div style={{ ...styles.row, width: '32%' }}>
                            <TextInput
                                label={ 'Stop Loss Charge Definition' }
                                value={ formState.experienceAnalysis.ehc.stopLossChargeDefinition }
                                onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'ehc', 'stopLossChargeDefinition' ], event) }
                                error={ Boolean(errors?.experienceAnalysis?.ehc?.stopLossChargeDefinition)}
                                helperText={ errors?.experienceAnalysis?.ehc?.stopLossChargeDefinition }
                                selectOptions={[ 'of incurred claims', 'of paid premiums', 'none' ]}
                                select
                                fullWidth
                            />
                        </div>

                        <Text color={ 'primary' } customStyle={{ fontSize: 19, marginBottom: 8, marginTop: 10 }}>
                            Set For All Classes
                        </Text>

                        <Text small bold customStyle={{ marginTop: 10 }}>
                            Would you like to set the same Stop Loss Charge for all classes below?
                        </Text>

                        <BubbleSelectBlock
                            first={{
                                title: 'Yes',
                                selected: isSameStopLossChargeAllClasses === true,
                                field: 'Yes'
                            }}
                            second={{
                                title: 'No',
                                selected: isSameStopLossChargeAllClasses === false,
                                field: 'No'
                            }}
                            handleChange={(field) => _toggleSameStopLossCharge(field === 'Yes') }
                        />

                        {
                            isSameStopLossChargeAllClasses ?
                                <NumberInput
                                    label={ 'Stop Loss Charge' }
                                    value={ sameStopLossCharge }
                                    onChange={ (event) => _handleSameStopLossChargeChange(event.target.value) }
                                    suffix="%"
                                    fullWidth
                                    customStyle={{ width: '32%' }}
                                />
                                : null
                        }

                        {
                            Object.keys(formState.experienceAnalysis.classes).map((classID) => (
                                <div key={ classID } style={{ marginTop: 10 }}>
                                    <Text color={ 'primary'} customStyle={{ fontSize: 19 }}>
                                        { classes[classID].title }
                                    </Text>
                                    <div style={{ ...styles.row, width: '65.5%' }}>
                                        <NumberInput
                                            label={ 'Stop Loss Charge' }
                                            value={ formState.experienceAnalysis.classes[classID].ehcStopLossChargePercentage }
                                            onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'classes', `${ classID }`, 'ehcStopLossChargePercentage' ], event) }
                                            error={ Boolean(errors?.experienceAnalysis?.classes?.[classID]?.ehcStopLossChargePercentage)}
                                            helperText={ errors?.experienceAnalysis?.classes?.[classID]?.ehcStopLossChargePercentage }
                                            suffix="%"
                                            fullWidth
                                            disabled={ isSameStopLossChargeAllClasses }
                                        />

                                        <NumberInput
                                            label={ 'EHC Stop Loss Claims' }
                                            value={ formState.experienceAnalysis.classes[classID].ehcStopLossClaims }
                                            onChange={ (event) => _handleInputChange([ 'experienceAnalysis', 'classes', `${ classID }`, 'ehcStopLossClaims' ], event) }
                                            error={ Boolean(errors?.experienceAnalysis?.classes?.[classID]?.ehcStopLossClaims) }
                                            helperText={ errors?.experienceAnalysis?.classes?.[classID]?.ehcStopLossClaims }
                                            prefix="$"
                                            fullWidth
                                        />
                                    </div>
                                </div>
                            ))    
                        }

                        {
                            Object.entries(formState.experienceAnalysis.historicalExperienceAnalysis)
                                .sort(([ yearA ], [ yearB ]) => yearB - yearA)
                                .map(([ year, data ]) => (
                                    <RenewalHistoricalAccordion
                                        key={ year }
                                        year={ year }
                                        renewalData={ data }
                                        classMeta={ classes }
                                        handleInputChange={ _handleInputChange }
                                        errors={ errors }
                                    />
                                ))
                        }
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 30, marginBottom: 10, gap: 15 }}>
                        <Button
                            onClick={ close() }
                            transparent
                        >
                            Back to Renewal Overview
                        </Button>
                        <Button
                            color={ 'primary' }
                            onClick={ () => _submitRenewalForm() }
                        >
                            Submit
                        </Button>
                    </div>
                </ContentBlock>
            </Content>

            <Modal
                open={Boolean(showGeneratingModal)}
                onClose={ () => setShowGeneratingModal(false) }
                style={{ width: 670 }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Icon style={{ color: Theme.colors.skyBlue, fontSize: 100, marginBottom: 22 }}>
                        { Icons['Hour Glass'] }
                    </Icon>
                    <Text large bold margin="0px 0px 10px 0px">
                        Your Renewal Report is being generated
                    </Text>
                    <Text medium customStyle={{ textAlign: 'center' }}>
                        Please be patient, as it may take several minutes to complete. Once ready, we’ll email it to you and it will be visible in the renewal files!
                    </Text>
                </div>
            </Modal>
        </ContainerControlSite>
    );
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader
});

export default (connect(mapStateToProps)(RenewalForm));